import {ROUTE_PATHS} from 'constant';

export const currentRoute = () => window.location.pathname.replace(/(.*)\/$/, '$1');

const SAFE_LEGACY_ROUTE_PREFIXES = ['/goals'];

type RoutePattern = {
  pattern: string;
  parts: string[];
};

// Every optional segment is either present or dropped (e.g. /workstreams/:id?/:page?).
const expandOptionalParts = (parts: string[]): string[][] => {
  const optionalIndex = parts.findIndex((part) => part.endsWith('?'));

  if (optionalIndex === -1) {
    return [parts];
  }

  const requiredParts = [...parts];
  requiredParts[optionalIndex] = requiredParts[optionalIndex].slice(0, -1);
  const withoutOptionalPart = parts.filter((_, index) => index !== optionalIndex);

  return [...expandOptionalParts(requiredParts), ...expandOptionalParts(withoutOptionalPart)];
};

const expandOptionalSegments = (route: string): RoutePattern[] =>
  expandOptionalParts(route.split('/')).map((parts) => ({pattern: route, parts}));

const routePatterns: RoutePattern[] = Object.values(ROUTE_PATHS)
  .map((route) => (route.endsWith('?') ? route : route.split('?')[0]))
  .flatMap(expandOptionalSegments)
  .sort((a, b) => {
    const aWildcard = a.parts[a.parts.length - 1] === '*';
    const bWildcard = b.parts[b.parts.length - 1] === '*';
    const aDynamic = a.parts.filter((part) => part.startsWith(':')).length;
    const bDynamic = b.parts.filter((part) => part.startsWith(':')).length;
    return (
      Number(aWildcard) - Number(bWildcard) ||
      aDynamic - bDynamic ||
      b.parts.length - a.parts.length
    );
  });

const getPathname = (pathname: string): string => {
  const path = pathname.split(/[?#]/, 1)[0] || '/';
  return path.length > 1 ? path.replace(/\/$/, '') : path;
};

/**
 * Returns a route pattern without exposing user-controlled path segments.
 * Unknown paths are intentionally collapsed to `/unknown`.
 */
export const getAnonymizedRoute = (pathname = window.location.pathname): string => {
  const path = getPathname(pathname);
  const pathParts = path.split('/');

  for (const {pattern, parts: routeParts} of routePatterns) {
    if (routeParts[routeParts.length - 1] === '*') {
      if (pathParts.length < routeParts.length - 1) continue;
    } else if (routeParts.length !== pathParts.length) {
      continue;
    }

    const matches = routeParts.every(
      (part, index) => part === '*' || part.startsWith(':') || part === pathParts[index]
    );
    if (matches) return pattern;
  }

  if (
    SAFE_LEGACY_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  ) {
    return '/goals';
  }

  return '/unknown';
};

export const isProfileRoute = () => /\/people\/[\w]+$/.test(window.location.pathname);
export const isTeamRoute = () => /\/teams\/[-\w]+$/.test(window.location.pathname);
export const isWorkstreamRoute = () => /\/workstreams\/[\d]+$/.test(window.location.pathname);
export const isWorkstreamDetailRoute = () =>
  /\/workstreams\/[\d]+\/detail$/.test(window.location.pathname);
export const isWorkstreamActivityRoute = () =>
  /\/workstream\/[\d]+\/activity$/.test(window.location.pathname);
